export type FileData = string | Uint8Array;

export interface SnapshotFile {
  path: string;
  data: FileData;
  kind?: "file" | "symlink" | "device";
}

export type ProjectSnapshot = Readonly<Record<string, FileData>>;

export type DemoFramework =
  | "plain-static"
  | "vite-prebuilt"
  | "vite-react-prebuilt"
  | "create-react-app-prebuilt"
  | "astro-static-prebuilt"
  | "next-static-export-prebuilt"
  | "next-managed-runtime"
  | "parcel-prebuilt"
  | "sveltekit-static-prebuilt"
  | "react-router-spa-prebuilt"
  | "nuxt-static-prebuilt"
  | "gatsby-prebuilt"
  | "docusaurus-prebuilt"
  | "eleventy-prebuilt";

export type StaticPublishDirectory =
  | "."
  | "dist"
  | "build"
  | "out"
  | "build/client"
  | ".output/public"
  | "public"
  | "_site";

export type DemoBlockReason =
  | "AI_FIXED_ROUTE_REQUIRED"
  | "AI_LEGACY_ROUTE_FORBIDDEN"
  | "BUILD_EXECUTION_DISABLED"
  | "MALFORMED_PACKAGE_JSON"
  | "SERVER_RUNTIME_UNSUPPORTED"
  | "UNSUPPORTED_PROJECT";

export interface ProviderSourceFindings {
  anthropicMessages: boolean;
  openAIResponses: boolean;
  openAIChatCompletions: boolean;
}

export interface SupportedDemoBuildPlan {
  supported: true;
  framework: DemoFramework;
  publishDirectory: StaticPublishDirectory;
  execution: "none";
  providerSourceFindings: ProviderSourceFindings;
}

export interface BlockedDemoBuildPlan {
  supported: false;
  reasonCode: DemoBlockReason;
  message: string;
}

export type DemoBuildPlan = SupportedDemoBuildPlan | BlockedDemoBuildPlan;

export class CodedError<Code extends string = string> extends Error {
  readonly code: Code;

  constructor(code: Code, message: string) {
    super(message);
    this.name = "CodedError";
    this.code = code;
  }
}

export function byteLength(data: FileData): number {
  return typeof data === "string" ? new TextEncoder().encode(data).byteLength : data.byteLength;
}

export function fileText(data: FileData): string {
  return typeof data === "string" ? data : new TextDecoder("utf-8", { fatal: false, ignoreBOM: false }).decode(data);
}

export function stableRecordEntries<T>(record: Readonly<Record<string, T>>): Array<[string, T]> {
  return Object.entries(record).sort(([left], [right]) => left.localeCompare(right));
}

export type RequestBodyErrorCode = "BODY_TOO_LARGE" | "INVALID_JSON";

export class RequestBodyError extends CodedError<RequestBodyErrorCode> {
  constructor(code: RequestBodyErrorCode, message: string) {
    super(code, message);
    this.name = "RequestBodyError";
  }
}

export class BoundedBodyError extends CodedError<"BODY_TOO_LARGE"> {
  constructor(message: string) {
    super("BODY_TOO_LARGE", message);
    this.name = "BoundedBodyError";
  }
}

export async function readBodyBytes(body: ReadableStream<Uint8Array> | null, maxBytes: number): Promise<Uint8Array> {
  if (!body) return new Uint8Array(0);
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let totalBytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.byteLength;
      if (totalBytes > maxBytes) {
        await reader.cancel();
        throw new BoundedBodyError(`Body exceeds the ${maxBytes} byte limit.`);
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const bytes = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

export async function readJsonBody(request: Request, maxBytes: number): Promise<unknown> {
  const declaredLength = request.headers.get("content-length");
  if (declaredLength !== null) {
    const bytes = Number(declaredLength);
    if (!Number.isSafeInteger(bytes) || bytes < 0) {
      throw new RequestBodyError("INVALID_JSON", "Content-Length is invalid.");
    }
    if (bytes > maxBytes) {
      throw new RequestBodyError("BODY_TOO_LARGE", `JSON body exceeds the ${maxBytes} byte limit.`);
    }
  }

  if (!request.body) throw new RequestBodyError("INVALID_JSON", "Request body is required.");
  let body: Uint8Array;
  try {
    body = await readBodyBytes(request.body, maxBytes);
  } catch (error) {
    if (error instanceof BoundedBodyError) {
      throw new RequestBodyError("BODY_TOO_LARGE", `JSON body exceeds the ${maxBytes} byte limit.`);
    }
    throw error;
  }
  try {
    return JSON.parse(new TextDecoder().decode(body)) as unknown;
  } catch {
    throw new RequestBodyError("INVALID_JSON", "Request body is not valid JSON.");
  }
}
